import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  ListGroup,
  ListGroupItem,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import EnterpriseService from "../../common/services/enterprise-service";
import Loader from "../ui/Loader";
import { EnterpriseContext } from "./EnterpriseContext";
import { useNavigate } from "react-router";
import MemberBadges from "./MemberBadges";
import CompanyAwards from "./CompanyAwards";
import MileStatsCards from "./MileStatsCards";
import EcoStatsCard from "./EcoStatsCards";
import CustomBadge from "../ui/CustomBadge";
import truckyService from "../../common/services/trucky-service";
import CountUp from "react-countup";
import Chart from "react-apexcharts";
import PeriodSelector from "./PeriodSelector";
import UserDefinedUnits from "../../common/utils/UserDefinedUnits";

const moment = require("moment");
const momentDurationFormatSetup = require("moment-duration-format");

const MonthlyCompanyStatsInnerComponent = ({
  company,
  container,
  detailedJobsStats = false,
  enableNavigation = true,
  showAggregatedJobsChart = false,
}) => {
  momentDurationFormatSetup(container.localeManager.moment);

  const es = new EnterpriseService();
  const [stats, setStats] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState(
    enableNavigation
      ? truckyService.loadSearchParameters("companystats_currentPeriod2", {
          month: moment.utc().month(),
          year: moment.utc().year(),
          week: moment.utc().isoWeek(),
        })
      : {
          month: moment.utc().month(),
          year: moment.utc().year(),
          week: moment.utc().isoWeek(),
        },
  );
  const [loading, setLoading] = useState(false);
  const [ecoStats, setEcoStats] = useState(null);
  const [period, setPeriod] = useState(
    enableNavigation
      ? truckyService.loadSearchParameters("companystats_period", "monthly")
      : "monthly",
  );

  const [loadingChart, setLoadingChart] = useState(false);
  const [jobsGroupedByDate, setJobsGroupedByDate] = useState([]);

  const { globalContext } = useContext(EnterpriseContext);

  const member = globalContext.member;

  useEffect(() => {
    loadData();
  }, [period, currentPeriod.month, currentPeriod.year, currentPeriod.week]);

  useEffect(() => {
    if (enableNavigation)
      truckyService.saveSearchParameters("companystats_period", period);
  }, [period]);

  useEffect(() => {
    if (enableNavigation)
      truckyService.saveSearchParameters(
        "companystats_currentPeriod2",
        currentPeriod,
      );
  }, [currentPeriod.month, currentPeriod.year, currentPeriod.week]);

  const loadData = async () => {
    getStats();

    if (
      detailedJobsStats &&
      (company.company_type == "realistic" || company.company_type == "both")
    ) {
      getEcoStats();
    }

    if (showAggregatedJobsChart) {
      getJobsGroupedByDate();
    }
  };

  const getStats = async () => {
    let result;

    setLoading(true);

    if (period == "monthly" || period == "weekly")
      result = await es.getCompanyStatsMonthly(
        company.id,
        currentPeriod.month + 1,
        currentPeriod.year,
      );
    else
      result = await es.getCompanyStatsYearly(company.id, currentPeriod.year);

    setStats(result);
    setLoading(false);
  };

  const getEcoStats = async () => {
    let result;

    if (period == "monthly" || period == "weekly")
      result = await es.getEcoCompanyStatsMonthly(
        company.id,
        currentPeriod.month + 1,
        currentPeriod.year,
      );
    else
      result = await es.getEcoCompanyStatsYearly(
        company.id,
        currentPeriod.year,
      );

    setEcoStats(result);
  };

  const getJobsGroupedByDate = async () => {
    setLoadingChart(true);
    const filters = {
      period: period,
      month: currentPeriod.month + 1,
      year: currentPeriod.year,
      week: currentPeriod.week,
    };
    const result = await es.getCompanyJobsGroupedByDate(company.id, filters);
    setJobsGroupedByDate(result);
    setLoadingChart(false);
  };

  const renderJobsChart = () => {
    return (
      <Chart
        height={350}
        width={700}
        type="line"
        options={{
          chart: {
            redrawOnParentResize: true,
            type: "line",
            height: 350,
            width: 700,
            toolbar: {
              tools: {
                download: false,
              },
            },
          },
          grid: {
            borderColor: "#2b2b40",
          },
          stroke: {
            width: [0, 4],
          },
          dataLabels: {
            enabled: true,
            enabledOnSeries: [1],
          },
          labels: jobsGroupedByDate.map((m) => m.date),
          xaxis: {
            type: "datetime",
          },
          yaxis: {
            labels: {
              formatter: (value) =>
                container.localeManager.numeral(value).format("0,0") +
                " " +
                UserDefinedUnits.getDistanceUnitLabel(
                  globalContext.member,
                  null,
                ),
            },
          },
        }}
        series={[
          {
            name: container.localeManager.strings.totDistance,
            type: "column",
            data: jobsGroupedByDate.map((m) =>
              UserDefinedUnits.convertDistanceValue(
                member,
                null,
                m.total_distance_km,
              ),
            ),
          },
          {
            name: container.localeManager.strings.avg,
            type: "line",
            data: jobsGroupedByDate.map((m) =>
              UserDefinedUnits.convertDistanceValue(
                member,
                null,
                m.avg_distance_km,
              ),
            ),
          },
        ]}
      ></Chart>
    );
  };

  return (
    <>
      {!loading && enableNavigation && (
        <div class="members-toolbar">
          <PeriodSelector
            enableDaily={false}
            container={container}
            onChangeRange={setPeriod}
            onChangePeriod={setCurrentPeriod}
            state={{ range: period, currentPeriod: currentPeriod }}
          ></PeriodSelector>
        </div>
      )}
      {showAggregatedJobsChart && loadingChart && <Loader></Loader>}
      {showAggregatedJobsChart && !loadingChart && (
        <div className="border rounded p-3 d-flex justify-content-center my-3">
          {renderJobsChart()}
        </div>
      )}
      {!loading && stats != null && (
        <MileStatsCards
          period={period}
          container={container}
          stats={stats}
          company={company}
          ets2_preferred_distance_unit={company.preferred_distance_unit}
          ats_preferred_distance_unit={company.preferred_distance_unit}
        ></MileStatsCards>
      )}
      {(period == "monthly" || period == "weekly") && (
        <>
          <CompanyAwards
            company={company}
            month={currentPeriod.month + 1}
            year={currentPeriod.year}
            container={container}
          ></CompanyAwards>
          <MemberBadges
            company={company}
            month={currentPeriod.month + 1}
            year={currentPeriod.year}
            container={container}
          ></MemberBadges>
        </>
      )}
      {!loading && ecoStats != null && (
        <EcoStatsCard
          container={container}
          ecoStats={ecoStats}
          company={company}
          currency={company.currency}
          volume_unit={company.preferred_volume_unit}
          distance_unit={company.preferred_distance_unit}
          weight_unit={company.preferred_weight_unit}
        ></EcoStatsCard>
      )}
    </>
  );
};

export default MonthlyCompanyStatsInnerComponent;
